import { Scissors } from 'lucide-react';

export interface PruneBranchLiveItem {
  id: string;
  name: string;
  status: 'active' | 'pruned';
  value: number;
  impact: number;
  feasibility: number;
}

interface PruneBranchLiveProps {
  branches: PruneBranchLiveItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onTogglePrune: (id: string) => void;
  onMetricChange: (id: string, field: 'value' | 'impact' | 'feasibility', value: number) => void;
}

function MetricRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="font-heading text-[0.48rem] uppercase text-[var(--text-ter)] w-16">{label}</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`w-7 h-7 rounded text-[0.65rem] border ${
              value === n
                ? 'border-alpine-green-500/50 bg-alpine-green-500/20 text-alpine-green-400'
                : 'border-[var(--rule-bright)] text-[var(--text-sec)]'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

export function PruneBranchLive({
  branches,
  selectedId,
  onSelect,
  onTogglePrune,
  onMetricChange,
}: PruneBranchLiveProps) {
  const selected = branches.find(b => b.id === selectedId);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="space-y-2">
        {branches.map(b => (
          <button
            key={b.id}
            type="button"
            onClick={() => onSelect(b.id)}
            className={`w-full text-left flex items-center justify-between gap-2  border px-3 py-2.5 ${
              selectedId === b.id
                ? 'border-electric-mustard-600/40 bg-electric-mustard-600/8'
                : b.status === 'pruned'
                  ? 'border-[var(--rule)] bg-[var(--bg-raised)] opacity-50'
                  : 'border-[var(--rule-bright)] bg-[var(--surface)]'
            }`}
          >
            <span className="font-body text-[0.8rem] text-[var(--text-sec)] line-clamp-1">{b.name}</span>
            <span
              className={`font-heading text-[0.45rem] uppercase ${
                b.status === 'pruned' ? 'text-[var(--text-ter)]' : 'text-alpine-green-400'
              }`}
            >
              {b.status}
            </span>
          </button>
        ))}
      </div>

      {selected && (
        <div className="border border-[var(--rule)] bg-[var(--surface)] p-4">
          <div className="font-body text-[0.85rem] font-medium text-[var(--text-pri)] mb-3">{selected.name}</div>
          <div className="space-y-3 mb-4">
            <MetricRow label="Value" value={selected.value} onChange={v => onMetricChange(selected.id, 'value', v)} />
            <MetricRow label="Impact" value={selected.impact} onChange={v => onMetricChange(selected.id, 'impact', v)} />
            <MetricRow
              label="Feasible"
              value={selected.feasibility}
              onChange={v => onMetricChange(selected.id, 'feasibility', v)}
            />
          </div>
          <button
            type="button"
            onClick={() => onTogglePrune(selected.id)}
            className="inline-flex items-center gap-1.5 font-heading text-[0.55rem] tracking-[0.08em] uppercase text-luminous-coral-700 border border-luminous-coral-700/35 px-3 py-2 hover:bg-luminous-coral-700/10"
          >
            <Scissors className="h-3.5 w-3.5" />
            {selected.status === 'active' ? 'Prune branch' : 'Restore branch'}
          </button>
        </div>
      )}
    </div>
  );
}
